import type {FoniRow} from '../../lib/lvs-foni'
import {LVS_H_OPTIONS} from '../../lib/lvs-heights'
import {MonoLabel} from '../ui/mono-label'
import {UnderlineInput, UnderlineSelect} from '../ui/underline-field'

const VARIANTS: Array<'A' | 'B'> = ['A', 'B']

export interface FoniRowEditorProps {
    readonly rows: readonly FoniRow[]
    readonly onChange: (rows: readonly FoniRow[]) => void
}

/**
 * Controlled row editor: text + h + variant per row, plus add/remove.
 * Canonical UI for editing a foni row list. Used by FoniPage directly
 * and by the konstruktors wizard's ContentStep.
 */
export function FoniRowEditor({rows, onChange}: FoniRowEditorProps) {
    const update = (i: number, patch: Partial<FoniRow>) =>
        onChange(rows.map((r, idx) => (idx === i ? {...r, ...patch} : r)))
    const add = () => onChange([...rows, {text: '', h: 250, variant: 'A'}])
    const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i))

    return (
        <div className='space-y-2'>
            <MonoLabel>Rindas</MonoLabel>
            {rows.map((row, i) => (
                <div
                    key={i}
                    className='flex flex-wrap gap-3 items-center'
                >
                    <UnderlineInput
                        type='text'
                        value={row.text}
                        onChange={e => update(i, {text: e.target.value})}
                        className='flex-1 min-w-[200px]'
                    />
                    <UnderlineSelect
                        value={row.h}
                        onChange={e => update(i, {h: Number(e.target.value)})}
                    >
                        {LVS_H_OPTIONS.map(h => (
                            <option
                                key={h}
                                value={h}
                            >{`h=${h}`}</option>
                        ))}
                    </UnderlineSelect>
                    <UnderlineSelect
                        value={row.variant}
                        onChange={e => update(i, {variant: e.target.value as 'A' | 'B'})}
                    >
                        {VARIANTS.map(v => (
                            <option
                                key={v}
                                value={v}
                            >{`raksts ${v}`}</option>
                        ))}
                    </UnderlineSelect>
                    <button
                        type='button'
                        onClick={() => remove(i)}
                        disabled={rows.length === 1}
                        className='font-mono text-xs tracking-[0.2em] uppercase text-foreground/50 hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1'
                    >
                        Dzēst
                    </button>
                </div>
            ))}
            <button
                type='button'
                onClick={add}
                className='font-mono text-xs tracking-[0.2em] uppercase text-primary hover:underline'
            >
                + Pievienot rindu
            </button>
        </div>
    )
}
